import { create } from 'zustand';
import type { User } from '../hooks/useAuth';
import { setCookie, getCookie, deleteCookie, hasValidToken } from '../lib/cookies';
import { Logger } from '../utils/logger';

interface AuthState {
  // Authentication state
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (user: Partial<User>) => void;
  initializeAuth: () => void;
}

// Initialize auth state immediately on store creation
const initializeAuthState = () => {
  const globalWindow = typeof globalThis !== 'undefined' ? (globalThis as any).window : undefined;
  if (!globalWindow) {
    return { user: null, token: null, isAuthenticated: false };
  }
  
  const token = getCookie('gravity_token');
  Logger.debug('AuthStore', 'Auth initialization - token found:', !!token);
  
  if (token && hasValidToken()) {
    Logger.debug('AuthStore', 'Auth initialization - token valid, user authenticated');
    return { 
      user: null, // Will be loaded later via API call
      token, 
      isAuthenticated: true 
    };
  } else {
    // Clean up invalid token
    deleteCookie('gravity_token');
    Logger.debug('AuthStore', 'Auth initialization - no valid token, user not authenticated');
    return { user: null, token: null, isAuthenticated: false };
  }
};

const initialState = initializeAuthState();

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state from cookie
  user: initialState.user,
  token: initialState.token,
  isAuthenticated: initialState.isAuthenticated,
  isLoading: false,
  
  // Actions
  setAuth: (user, token) => {
    // Store token in cookie with 7 days expiry
    setCookie('gravity_token', token, {
      expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
      secure:
        typeof globalThis !== 'undefined' &&
        ((globalThis as any).location?.protocol === 'https:' ||
          (globalThis as any).window?.location?.protocol === 'https:'),
      sameSite: 'lax',
    });
    
    set({ 
      user, 
      token, 
      isAuthenticated: true, 
      isLoading: false 
    });
  },
  
  clearAuth: () => {
    // Remove token from cookie
    deleteCookie('gravity_token');
    
    set({ 
      user: null, 
      token: null, 
      isAuthenticated: false, 
      isLoading: false 
    });
  },
  
  setLoading: (loading) => set({ isLoading: loading }),
  
  updateUser: (userData) => {
    const currentUser = get().user;
    if (currentUser) {
      set({ user: { ...currentUser, ...userData } });
    }
  },
  
  initializeAuth: () => {
    const token = getCookie('gravity_token');
    
    if (token && hasValidToken()) {
      set({ 
        token, 
        isAuthenticated: true,
        isLoading: false 
      });
    } else {
      // Clean up invalid token
      deleteCookie('gravity_token');
      set({ 
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false 
      });
    }
  },
}));
